<template>
  <div
    ref="selectWrapper"
    v-click-outside:[popperPaneRef]="handleClose"
    :class="wrapperKls"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
    @click.stop="toggleMenu"
  >
    <jd-tooltip
      ref="tooltipRef"
      :visible="dropMenuVisible"
      :placement="placement"
      :teleported="teleported"
      :popper-class="[nsSelect.e('popper'), popperClass]"
      :popper-options="popperOptions"
      :fallback-placements="fallbackPlacements"
      :effect="effect"
      pure
      trigger="click"
      :transition="`${nsSelect.namespace.value}-zoom-in-top`"
      :stop-popper-mouse-event="false"
      :gpu-acceleration="false"
      :persistent="persistent"
      :show-arrow="false"
      :offset="4"
      v-bind="tooltipProps"
      @show="handleMenuEnter"
    >
      <template #default>
        <div
          class="select-trigger"
          @mouseenter="inputHovering = true"
          @mouseleave="inputHovering = false"
        >
          <div
            v-if="multiple"
            ref="tags"
            tabindex="-1"
            :class="tagsKls"
            :style="selectTagsStyle"
            @click="focus"
          >
            <transition
              v-if="collapseTags && selected.length"
              @after-leave="resetInputHeight"
            >
              <span
                :class="tagWrapperKls"
                :style="{
                  maxWidth: $slots.prefix
                    ? inputWidth - 70 + 'px'
                    : inputWidth - 46 + 'px',
                  marginLeft:
                    $slots.prefix &&
                    ((prefixWidth && selected.length) || tagInMultiLine)
                      ? `${prefixWidth}px`
                      : '',
                }"
              >
                <slot name="tag">
                  <jd-tag
                    v-for="item in showTagList"
                    :key="getValueKey(item)"
                    :closable="!selectDisabled && !item.isDisabled"
                    :size="collapseTagSize"
                    :hit="item.hitState"
                    :type="tagType"
                    :round="round"
                    disable-transitions
                    @close="deleteTag($event, item)"
                  >
                    <span
                      :class="nsSelect.e('tags-text')"
                      :style="tagTextStyle"
                      :title="item.currentLabel"
                    >
                     {{ item.currentLabel }}
                    </span>
                  </jd-tag>
                  <jd-tag
                    v-if="
                      selected.length > maxCollapseTags && multipleLimit === 0"
                    :closable="false"
                    :round="round"
                    :size="collapseTagSize"
                    :type="tagType"
                    disable-transitions
                  >
                    <jd-tooltip
                      v-if="collapseTagsTooltip"
                      ref="tagTooltipRef"
                      :disabled="dropMenuVisible"
                      :fallback-placements="['bottom', 'top', 'right', 'left']"
                      :effect="effect"
                      placement="bottom"
                      :teleported="teleported"
                    >
                      <template #default>
                        <span :class="nsSelect.e('tags-text')"
                          >+ {{ selected.length - maxCollapseTags }}</span
                        >
                      </template>
                      <template #content>
                        <div :class="nsSelect.e('collapse-tags')">
                          <div
                            v-for="item in collapseTagList"
                            :key="getValueKey(item)"
                            :class="nsSelect.e('collapse-tag')"
                          >
                            <jd-tag
                              class="in-tooltip"
                              :closable="!selectDisabled && !item.isDisabled"
                              :size="collapseTagSize"
                              :hit="item.hitState"
                              :type="tagType"
                              :round="round"
                              disable-transitions
                              :style="{ margin: '2px' }"
                              @close="handleDeleteTooltipTag($event, item)"
                            >
                              <span
                                :class="nsSelect.e('tags-text')"
                                :title="item.currentLabel"
                                >{{ item.currentLabel }}</span
                              >
                            </jd-tag>
                          </div>
                        </div>
                      </template>
                    </jd-tooltip>
                    <span
                      v-if="!collapseTagsTooltip && multipleLimit === 0"
                      :class="nsSelect.e('tags-text')"
                      >+ {{ selected.length - maxCollapseTags }}</span
                    >
                  </jd-tag>
                </slot>
              </span>
            </transition>
            <template v-if="multipleLimit > 0 && selected.length > 0">
              <jd-tag
                class="is-multipleLimit"
                :closable="false"
                :round="round"
                :size="collapseTagSize"
                :type="tagType"
                disable-transitions
              >
                <span :class="nsSelect.e('tags-text')">
                  {{ selected.length }}/{{ multipleLimit }}
                </span>
              </jd-tag>
            </template>
            <transition v-if="!collapseTags" @after-leave="resetInputHeight">
              <span
                :class="tagWrapperKls"
                :style="{
                  maxWidth: $slots.prefix
                    ? multipleLimit > 0
                      ? inputWidth - 96 + 'px'
                      : inputWidth - 70 + 'px'
                    : multipleLimit > 0
                    ? inputWidth - 72 + 'px'
                    : inputWidth - 46 + 'px',
                  marginLeft:
                    $slots.prefix &&
                    ((prefixWidth && selected.length) || tagInMultiLine)
                      ? `${prefixWidth}px`
                      : '',
                }"
              >
                <slot name="tag">
                  <jd-tag
                    v-for="item in selected"
                    :key="getValueKey(item)"
                    :closable="!selectDisabled && !item.isDisabled"
                    :size="collapseTagSize"
                    :hit="item.hitState"
                    :type="tagType"
                    :round="round"
                    disable-transitions
                    @close="deleteTag($event, item)"
                  >
                    <span
                      :class="nsSelect.e('tags-text')"
                      :style="{
                        maxWidth:
                          multipleLimit > 0
                            ? inputWidth - 120 + 'px'
                            : inputWidth - 75 + 'px',
                      }"
                      :title="item.currentLabel"
                      >{{ item.currentLabel }}</span
                    >
                  </jd-tag>
                </slot>
              </span>
            </transition>
            <input
              v-if="filterable && !selectDisabled"
              ref="input"
              v-model="query"
              type="text"
              :class="inputKls"
              :disabled="selectDisabled"
              :autocomplete="autocomplete"
              :style="inputStyle"
              role="combobox"
              :aria-activedescendant="hoverOption?.id || ''"
              :aria-controls="contentId"
              :aria-expanded="dropMenuVisible"
              :aria-label="ariaLabel"
              aria-autocomplete="none"
              aria-haspopup="listbox"
              @focus="handleFocus"
              @blur="handleBlur"
              @keyup="managePlaceholder"
              @keydown="resetInputState"
              @keydown.down.prevent="navigateOptions('next')"
              @keydown.up.prevent="navigateOptions('prev')"
              @keydown.esc="handleKeydownEscape"
              @keydown.enter.stop.prevent="selectOption"
              @keydown.delete="deletePrevTag"
              @keydown.tab="visible = false"
              @compositionstart="handleComposition"
              @compositionupdate="handleComposition"
              @compositionend="handleComposition"
              @input="debouncedQueryChange"
            />
          </div>
          <div
            v-if="!multiple && showCustom"
            ref="customWrapper"
            :class="nsSelect.e('custom')"
          >
            <transition
              v-if="!multiple && showCustom"
              name="custom-wrapper"
              @after-leave="
                () => {
                  console.info('after-leave >>>')
                }
              "
            >
              <div
                :class="nsSelect.b('custom-wrapper')"
                :style="{
                  maxWidth: $slots.prefix
                    ? inputWidth - 70 + 'px'
                    : inputWidth - 46 + 'px',
                  marginLeft:
                    $slots.prefix && (prefixWidth || customInMultiLine)
                      ? `${prefixWidth}px`
                      : '',
                }"
              >
                <slot name="custom" />
              </div>
            </transition>
          </div>
          <div style="cursor: pointer;"
            v-if="!multiple && showWrapSelected"
            ref="wrapSelectedWrapper"
            :class="nsSelect.e('custom')"
          >
            <transition
              v-if="!multiple && showWrapSelected"
              name="custom-wrapper"
            >
              <div
                :class="[nsSelect.b('custom-wrapper')]"
                :style="{
                  maxWidth: $slots.prefix
                    ? inputWidth - 70 + 'px'
                    : inputWidth - 36 + 'px',
                  marginLeft:
                    $slots.prefix && prefixWidth ? `${prefixWidth}px` : '',
                }"
              >
                <span v-if="selectedLabel" :class="nsSelect.e('wrap-selected-text')"
                >
                  <jd-tooltip :content="selectedLabel" effect="light" placement="top">
                    {{ selectedLabel }}
                  </jd-tooltip>
                </span>
                <span v-else :class="[nsSelect.e('wrap-placeholder')]"
                >
                  {{ wrapPlaceholder }}
                </span>
              </div>
            </transition>
          </div>
          <!-- fix: https://github.com/jdesign-vue/jdesign-vue/issues/11415 -->
          <jd-input
            v-if="isIOS && !multiple && filterable && readonly"
            ref="iOSInput"
            :class="iOSInputKls"
            :disabled="selectDisabled"
            type="text"
            role="combobox"
            :aria-activedescendant="hoverOption?.id || ''"
            :aria-controls="contentId"
            :aria-expanded="dropMenuVisible"
            :label="ariaLabel"
            aria-autocomplete="none"
            aria-haspopup="listbox"
          >
          <template #append v-if="$slots.append" >
             <slot name="append"></slot>
          </template>
          </jd-input>
          <jd-input
            :id="id"
            ref="reference"
            v-model="selectedLabel"
            type="text"
            :placeholder="
              typeof currentPlaceholder === 'function'
                ? currentPlaceholder()
                : currentPlaceholder
            "
            :name="name"
            :autocomplete="autocomplete"
            :size="selectSize"
            :disabled="selectDisabled"
            :readonly="readonly"
            :validate-event="false"
            :class="[
              nsSelect.is('focus', visible),
              nsSelect.is('custom', showCustom),
              nsSelect.is('wrap-selected', showWrapSelected || wrapSelectedWrapper),
            ]"
            :tabindex="multiple && filterable ? -1 : undefined"
            :surface="surface"
            :round="round"
            :label="
              selected?.length
                ? selected.map((item) => item.currentLabel).join(',')
                : ariaLabel
            "
            role="combobox"
            :aria-activedescendant="hoverOption?.id || ''"
            :aria-controls="contentId"
            :aria-expanded="dropMenuVisible"
            aria-autocomplete="none"
            aria-haspopup="listbox"
            @focus="handleFocus"
            @blur="handleBlur"
            @input="debouncedOnInputChange"
            @paste="debouncedOnInputChange"
            @compositionstart="handleComposition"
            @compositionupdate="handleComposition"
            @compositionend="handleComposition"
            @keydown.down.stop.prevent="navigateOptions('next')"
            @keydown.up.stop.prevent="navigateOptions('prev')"
            @keydown.enter.stop.prevent="selectOption"
            @keydown.esc="handleKeydownEscape"
            @keydown.tab="visible = false"
          >
            <template v-if="$slots.prefix" #prefix>
              <div
                style="
                  height: 100%;
                  display: flex;
                  justify-content: center;
                  align-items: center;
                "
              >
                <slot name="prefix" />
              </div>
            </template>
            <template #suffix>
              <div
                v-if="$slots.suffix && !showClose"
                style="
                  height: 100%;
                  display: flex;
                  justify-content: center;
                  align-items: center;
                "
              >
                <slot name="suffix" />
              </div>
              <jd-icon
                v-if="iconComponent && !showClose"
                :class="[nsSelect.e('caret'), nsSelect.e('icon'), iconReverse]"
              >
                <component :is="iconComponent" />
              </jd-icon>
              <jd-icon
                v-if="showClose && clearIcon"
                :class="[nsSelect.e('caret'), nsSelect.e('icon')]"
                @click="handleClearClick"
              >
                <component :is="clearIcon" />
              </jd-icon>
            </template>
            <template v-if="fitContent" #fit-content-wrapper>
              {{ selectedLabel }}
            </template>
            <template #append v-if="$slots.append" >
             <slot name="append"></slot>
          </template>
          </jd-input>
        </div>
      </template>
      <template #content>
        <jd-select-menu :size="selectSize">
          <div v-if="$slots.header" :class="nsSelect.be('dropdown', 'header')">
            <slot name="header" />
          </div>
          <jd-scrollbar
            v-show="options.size > 0 && !loading"
            :id="contentId"
            ref="scrollbar"
            tag="ul"
            :wrap-class="nsSelect.be('dropdown', 'wrap')"
            :view-class="nsSelect.be('dropdown', 'list')"
            :class="scrollbarKls"
            role="listbox"
            :aria-label="ariaLabel"
            aria-orientation="vertical"
          >
            <slot v-if="showSelectAll" name="multiple-all">
              <jd-option-all
                :disabled="multipleAllDisabled"
                :text="multipleAllText"
              />
            </slot>
            <jd-option v-if="showNewOption" :value="query" :created="true" />
            <jd-options @update-options="onOptionsRendered">
              <slot />
            </jd-options>
          </jd-scrollbar>
          <template
            v-if="
              emptyText &&
              (!allowCreate || loading || (allowCreate && options.size === 0))
            "
          >
            <slot v-if="$slots.empty" name="empty" />
            <p v-else :class="nsSelect.be('dropdown', 'empty')">
              {{ emptyText }}
            </p>
          </template>
          <div v-if="$slots.extra" :class="nsSelect.be('dropdown', 'extra')">
            <slot name="extra" />
          </div>
        </jd-select-menu>
      </template>
    </jd-tooltip>
  </div>
</template>

<script lang="ts">
// @ts-nocheck
import {
  computed,
  defineComponent,
  nextTick,
  onMounted,
  provide,
  reactive,
  toRefs,
  unref,
} from 'vue'
import { useResizeObserver } from '@vueuse/core'
import { placements } from '@popperjs/core'
import { AngleUp as ArrowUp, Close as CircleClose } from '@jd/jdesign-icons-vue'
import { ClickOutside } from '@jdesign-vue/directives'
import { useId, useLocale, useNamespace } from '@jdesign-vue/hooks'
import JdInput from '@jdesign-vue/components/input'
import JdTooltip, {
  useTooltipContentProps,
} from '@jdesign-vue/components/tooltip'
import JdScrollbar from '@jdesign-vue/components/scrollbar'
import JdTag, { tagProps } from '@jdesign-vue/components/tag'
import JdIcon from '@jdesign-vue/components/icon'
import { CHANGE_EVENT, UPDATE_MODEL_EVENT } from '@jdesign-vue/constants'
import {
  definePropType,
  iconPropType,
  isIOS,
  isValidComponentSize,
} from '@jdesign-vue/utils'
import Item from '@jdesign-vue/components/steps/src/item.vue'
import JdOption from './option.vue'
import JdSelectMenu from './select-dropdown.vue'
import { useSelect, useSelectStates } from './useSelect'
import { selectKey } from './token'
import JdOptions from './options'
import JdOptionAll from './option-all.vue'

import type { PropType } from 'vue'
import type { ComponentSize } from '@jdesign-vue/constants'
import type { Placement } from '@popperjs/core'
import type { SelectContext } from './token'

const COMPONENT_NAME = 'JdSelect'
export default defineComponent({
  name: COMPONENT_NAME,
  componentName: COMPONENT_NAME,
  components: {
    JdInput,
    JdSelectMenu,
    JdOption,
    JdOptionAll,
    JdOptions,
    JdTag,
    JdScrollbar,
    JdTooltip,
    JdIcon,
  },
  directives: { ClickOutside },
  props: {
    name: String,
    id: String,
    modelValue: {
      type: [Array, String, Number, Boolean, Object],
      default: undefined,
    },
    autocomplete: {
      type: String,
      default: 'off',
    },
    automaticDropdown: Boolean,
    size: {
      type: String as PropType<ComponentSize>,
      validator: isValidComponentSize,
    },
    effect: {
      type: String as PropType<'light' | 'dark' | string>,
      default: 'light',
    },
    disabled: Boolean,
    clearable: Boolean,
    filterable: Boolean,
    allowCreate: Boolean,
    loading: Boolean,
    popperClass: {
      type: String,
      default: '',
    },
    popperOptions: {
      type: Object as PropType<Partial<Options>>,
      default: () => ({} as Partial<Options>),
    },
    remote: Boolean,
    loadingText: String,
    noMatchText: String,
    noDataText: String,
    remoteMethod: Function,
    filterMethod: Function,
    multiple: Boolean,
    multipleLimit: {
      type: Number,
      default: 0,
    },
    placeholder: {
      type: String,
    },
    defaultFirstOption: Boolean,
    reserveKeyword: {
      type: Boolean,
      default: true,
    },
    valueKey: {
      type: String,
      default: 'value',
    },
    collapseTags: Boolean,
    collapseTagsTooltip: {
      type: Boolean,
      default: false,
    },
    maxCollapseTags: {
      type: Number,
      default: 1,
    },
    teleported: useTooltipContentProps.teleported,
    persistent: {
      type: Boolean,
      default: true,
    },
    clearIcon: {
      type: iconPropType,
      default: CircleClose,
    },
    fitInputWidth: {
      type: Boolean,
      default: false,
    },
    suffixIcon: {
      type: iconPropType,
      default: ArrowUp,
    },
    surface: {
      type: Boolean,
      default: false,
    },
    round: {
      type: Boolean,
      default: false,
    },
    // eslint-disable-next-line vue/require-prop-types
    tagType: { ...tagProps.type, default: 'gray' },
    validateEvent: {
      type: Boolean,
      default: true,
    },
    remoteShowSuffix: {
      type: Boolean,
      default: false,
    },
    suffixTransition: {
      type: Boolean,
      default: true,
    },
    panelMaxHeight: {
      type: [String, Number],
      default: '',
    },
    placement: {
      type: String,
      values: placements,
      default: 'bottom-start',
    },
    fallbackPlacements: {
      type: definePropType<Placement[]>(Array),
      default: () => ['bottom-start', 'top-start', 'right', 'left'],
    },
    /**
     * @description native input aria-label
     */
    ariaLabel: {
      type: String,
      default: undefined,
    },
    tagsPopperClass: {
      type: String,
      default: '',
    },
    rawValues: {
      type: Array,
    },
    multipleAll: {
      type: Boolean,
      default: true,
    },
    multipleAllText: {
      type: String,
    },
    multipleAllDisabled: {
      type: Boolean,
    },
    fitContent: {
      type: Boolean,
    },
    tooltipProps: {
      type: Object as PropType<JdTooltipProps>,
    },
    /**
     * @description 单选模式下，开启后选择值折行显示
     */
    wrapSelected: {
      type: Boolean,
      default: false,
    },
    /**
     * @description 折行显示时的额外宽度补偿，默认150px
     */
    dropdownMaxWidth: {
      type: Number,
      default: 0,
    },
    noWrap: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    UPDATE_MODEL_EVENT,
    CHANGE_EVENT,
    'remove-tag',
    'clear',
    'visible-change',
    'focus',
    'blur',
    'raw-change',
  ],

  setup(props, ctx) {
    const nsSelect = useNamespace('select')
    const nsInput = useNamespace('input')
    const { t } = useLocale()
    const contentId = useId()
    const states = useSelectStates(props)
    const {
      optionList,
      optionsArray,
      hoverOption,
      selectSize,
      readonly,
      handleResize,
      collapseTagSize,
      debouncedOnInputChange,
      debouncedQueryChange,
      deletePrevTag,
      deleteTag,
      deleteSelected,
      handleOptionSelect,
      scrollToOption,
      setSelected,
      resetInputHeight,
      managePlaceholder,
      showClose,
      selectDisabled,
      iconComponent,
      iconReverse,
      showNewOption,
      emptyText,
      toggleLastOptionHitState,
      resetInputState,
      handleComposition,
      onOptionCreate,
      onOptionDestroy,
      handleMenuEnter,
      handleFocus,
      focus,
      blur,
      handleBlur,
      handleClearClick,
      handleClose,
      handleKeydownEscape,
      toggleMenu,
      selectOption,
      getValueKey,
      navigateOptions,
      handleDeleteTooltipTag,
      dropMenuVisible,

      reference,
      input,
      iOSInput,
      tooltipRef,
      tagTooltipRef,
      tags,
      selectWrapper,
      scrollbar,
      queryChange,
      groupQueryChange,
      handleMouseEnter,
      handleMouseLeave,
      showTagList,
      collapseTagList,
      // computed style
      selectTagsStyle,

      showSelectAll,
      handleOptionSelectAll,
      itemAllSelected,
      itemAllIndeterminate,
      updateSelectAllStatus,

      customWrapper,
      showCustom,
      wrapSelectedWrapper,
      showWrapSelected,
      resetWrapSelectedInputHeight,
    } = useSelect(props, states, ctx)

    const {
      inputWidth,
      selected,
      inputLength,
      filteredOptionsCount,
      visible,
      selectedLabel,
      hoverIndex,
      query,
      inputHovering,
      currentPlaceholder,
      wrapPlaceholder,
      menuVisibleOnFocus,
      isOnComposition,
      options,
      cachedOptions,
      optionsCount,
      prefixWidth,
      tagInMultiLine,
      customInMultiLine,
    } = toRefs(states)

    const wrapperKls = computed(() => {
      const classList = [nsSelect.b()]
      const _selectSize = unref(selectSize)
      if (_selectSize) {
        classList.push(nsSelect.m(_selectSize))
      }
      if (props.disabled) {
        classList.push(nsSelect.m('disabled'))
      }
      if (props.surface) {
        classList.push(nsSelect.is('surface', true))
      }
      if (props.filterable) {
        classList.push(nsSelect.is('filterable', true))
      }
      return classList
    })

    const tagsKls = computed(() => [
      nsSelect.e('tags'),
      nsSelect.is('disabled', unref(selectDisabled)),
    ])

    const tagWrapperKls = computed(() => [
      nsSelect.b('tags-wrapper'),
      { 'has-prefix': unref(prefixWidth) && unref(selected).length },
      nsSelect.is('noWrap', props.noWrap && props.collapseTags),
    ])

    const inputKls = computed(() => [
      nsSelect.e('input'),
      nsSelect.is(unref(selectSize)),
      nsSelect.is('disabled', unref(selectDisabled)),
    ])

    const iOSInputKls = computed(() => [
      nsSelect.e('input'),
      nsSelect.is(unref(selectSize)),
      nsSelect.em('input', 'iOS'),
    ])

    const scrollbarKls = computed(() => [
      nsSelect.is(
        'empty',
        !props.allowCreate &&
          Boolean(unref(query)) &&
          unref(filteredOptionsCount) === 0
      ),
    ])

    const tagTextStyle = computed(() => {
      const maxWidth = props.filterable
        ? unref(inputWidth) - 70
        : selected.value.length > 1
        ? unref(inputWidth) - 105
        : props.multipleLimit > 0
        ? unref(inputWidth) - 120
        : unref(inputWidth) - 85
      return { maxWidth: `${maxWidth}px` }
    })

    const inputStyle = computed(() => ({
      marginLeft:
        unref(prefixWidth) && ctx.slots.prefix && !selected.value.length
          ? `${prefixWidth.value}px`
          : '',
      flexGrow: 1,
      width: `64px`,
      maxWidth: `${unref(inputWidth) - 42}px`,
    }))

    provide(
      selectKey,
      reactive({
        props,
        options,
        optionsArray,
        cachedOptions,
        optionsCount,
        filteredOptionsCount,
        hoverIndex,
        handleOptionSelect,
        onOptionCreate,
        onOptionDestroy,
        selectWrapper,
        selected,
        setSelected,
        queryChange,
        groupQueryChange,

        handleOptionSelectAll,
        itemAllSelected,
        itemAllIndeterminate,
      }) as unknown as SelectContext
    )

    onMounted(() => {
      states.cachedPlaceHolder =
        currentPlaceholder.value =
        wrapPlaceholder.value =
          props.placeholder || t('el.select.placeholder')
      if (
        props.multiple &&
        Array.isArray(props.modelValue) &&
        props.modelValue.length > 0
      ) {
        currentPlaceholder.value = ''
      }
      useResizeObserver(selectWrapper, handleResize)
      if (props.remote && props.multiple) {
        resetInputHeight()
      }
      nextTick(() => {
        const refEl = reference.value && reference.value.$el
        if (!refEl) return
        inputWidth.value = refEl.getBoundingClientRect().width

        if (ctx.slots.prefix) {
          const prefix = refEl.querySelector(`.${nsInput.e('prefix')}`)
          prefixWidth.value = Math.max(
            prefix.getBoundingClientRect().width + 12,
            30
          )
        }
      })
      setSelected()

      if (props.multiple && props.multipleAll) {
        updateSelectAllStatus()
      }
    })

    if (props.multiple && !Array.isArray(props.modelValue)) {
      ctx.emit(UPDATE_MODEL_EVENT, [])
    }
    if (!props.multiple && Array.isArray(props.modelValue)) {
      ctx.emit(UPDATE_MODEL_EVENT, '')
    }

    const popperPaneRef = computed(() => {
      return tooltipRef.value?.popperRef?.contentRef
    })

    const onOptionsRendered = (v) => {
      optionList.value = v
    }

    return {
      isIOS,
      onOptionsRendered,
      tagInMultiLine,
      prefixWidth,
      selectSize,
      readonly,
      handleResize,
      collapseTagSize,
      debouncedOnInputChange,
      debouncedQueryChange,
      deletePrevTag,
      deleteTag,
      handleDeleteTooltipTag,
      deleteSelected,
      handleOptionSelect,
      scrollToOption,
      inputWidth,
      selected,
      inputLength,
      filteredOptionsCount,
      visible,
      selectedLabel,
      hoverIndex,
      query,
      inputHovering,
      currentPlaceholder,
      wrapPlaceholder,
      menuVisibleOnFocus,
      isOnComposition,
      options,
      resetInputHeight,
      managePlaceholder,
      showClose,
      selectDisabled,
      iconComponent,
      iconReverse,
      showNewOption,
      emptyText,
      toggleLastOptionHitState,
      resetInputState,
      handleComposition,
      handleMenuEnter,
      handleFocus,
      focus,
      blur,
      handleBlur,
      handleClearClick,
      handleClose,
      handleKeydownEscape,
      toggleMenu,
      selectOption,
      getValueKey,
      navigateOptions,
      dropMenuVisible,

      reference,
      input,
      iOSInput,
      tooltipRef,
      popperPaneRef,
      tags,
      selectWrapper,
      scrollbar,

      wrapperKls,
      tagsKls,
      tagWrapperKls,
      inputKls,
      iOSInputKls,
      scrollbarKls,
      selectTagsStyle,
      nsSelect,
      tagTextStyle,
      inputStyle,
      handleMouseEnter,
      handleMouseLeave,
      showTagList,
      collapseTagList,
      tagTooltipRef,

      showSelectAll,
      contentId,
      hoverOption,

      customWrapper,
      showCustom,
      customInMultiLine,
      wrapSelectedWrapper,
      showWrapSelected,
    }
  },
})
</script>
